export interface PullToRefreshState {
  touchStartY: number;
  touchStartAtTop: boolean;
  pullToRefreshTriggered: boolean;
  refreshDialogOpen: boolean;
  threshold: number;
  pullDistance: number;
  progress: number;
  isPulling: boolean;
}

export function createPullToRefreshState(threshold: number = 80): PullToRefreshState {
  return {
    touchStartY: 0,
    touchStartAtTop: false,
    pullToRefreshTriggered: false,
    refreshDialogOpen: false,
    threshold,
    pullDistance: 0,
    progress: 0,
    isPulling: false,
  };
}

export function isTouchEnvironment(): boolean {
  return typeof window !== 'undefined' && 'ontouchstart' in window;
}

/**
 * Get the main scroll container and its scrollTop.
 * In this app, the layout is inside <mat-sidenav-content> -> .content-container.
 * A mobile detail route releases those (ShellChromeService.bare) and the
 * document scrolls instead, so a candidate counts only while it actually
 * scrolls — otherwise its scrollTop is 0 at any depth and every downward
 * swipe would read as a pull from the top.
 */
export function getScrollContainer(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const candidates = ['mat-sidenav-content .content-container', 'mat-sidenav-content'];
  for (const selector of candidates) {
    const element = document.querySelector(selector) as HTMLElement | null;
    if (element && isScrollContainer(element)) {
      return element;
    }
  }

  const scrollingElement = document.scrollingElement as HTMLElement | null;
  if (scrollingElement) {
    return scrollingElement;
  }

  return document.body as HTMLElement | null;
}

function isScrollContainer(element: HTMLElement): boolean {
  const overflowY = getComputedStyle(element).overflowY;
  return overflowY === 'auto' || overflowY === 'scroll';
}

export function getScrollTop(): number {
  const container = getScrollContainer();
  if (container) {
    return container.scrollTop || 0;
  }

  if (typeof window !== 'undefined') {
    return window.scrollY || 0;
  }

  return 0;
}

const IGNORED_ROUTES = ['/customer-details/new-order'] as const;

export function handlePullToRefreshTouchStart(state: PullToRefreshState, event: TouchEvent): void {
  const currentPath = window.location.pathname;

  if (!isTouchEnvironment() || event.touches.length !== 1 || IGNORED_ROUTES.some((r) => currentPath.includes(r))) {
    return;
  }

  // Reset per-gesture state
  state.touchStartY = event.touches[0].screenY;
  state.pullDistance = 0;
  state.progress = 0;
  state.isPulling = false;
  state.pullToRefreshTriggered = false;

  const scrollTop = getScrollTop();
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight || 0 : 0;
  const inTopPortion = viewportHeight > 0 ? state.touchStartY <= viewportHeight * 0.7 : true;

  // Only allow pull-to-refresh gestures that start at the top of the scroll
  // AND in the top 30% of the visible screen.
  state.touchStartAtTop = scrollTop === 0 && inTopPortion;
  state.pullToRefreshTriggered = false;
}

export function handlePullToRefreshTouchMove(state: PullToRefreshState, event: TouchEvent): void {
  if (!isTouchEnvironment() || event.touches.length !== 1) {
    return;
  }

  const touch = event.touches[0];
  const deltaY = touch.screenY - state.touchStartY;
  const isSwipeDown = deltaY > 0;
  const scrollTop = getScrollTop();

  // Only care about downward swipes that STARTED and REMAIN at the top of the page
  if (!isSwipeDown || !state.touchStartAtTop || scrollTop > 0) {
    return;
  }

  // Update visual progress based on pull distance
  state.isPulling = true;
  state.pullDistance = Math.max(0, deltaY);
  state.progress = Math.max(0, Math.min(1, state.pullDistance / state.threshold));

  // If dialog is already open, just prevent further native pull-to-refresh where possible
  if (state.refreshDialogOpen) {
    event.preventDefault();
    return;
  }

  // Track whether threshold has been crossed during this gesture;
  // actual dialog opening is deferred to touchend so the user can cancel.
  if (!state.pullToRefreshTriggered && deltaY >= state.threshold) {
    state.pullToRefreshTriggered = true;
  } else if (state.pullToRefreshTriggered && deltaY < state.threshold) {
    // Allow user to cancel by moving back above the threshold
    state.pullToRefreshTriggered = false;
  }

  // Best-effort suppression of native pull-to-refresh (not guaranteed on all browsers)
  if (state.pullToRefreshTriggered) {
    event.preventDefault();
  }
}

export function handlePullToRefreshTouchEnd(state: PullToRefreshState): void {
  // Do not touch refreshDialogOpen; dialog flow is handled by caller
  state.pullDistance = 0;
  state.progress = 0;
  state.isPulling = false;
  state.pullToRefreshTriggered = false;
}

export function getPullProgressBucket(state: PullToRefreshState): number {
  const raw = Math.round(state.progress * 10);
  if (isNaN(raw)) {
    return 0;
  }
  return Math.max(0, Math.min(10, raw));
}
